import { createContext, useCallback, useContext, useMemo, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './api';
import type { TenantPermission } from '@loctopus/rbac';
import type { TenantStatus } from './types';

/** Real session context, backed by `GET /dashboard/me` (resolves the `loctopus_client_session`
 * cookie set by `POST /dashboard/auth/login`). */
interface AuthState {
  tenantId: string | null;
  email: string | null;
  permissions: TenantPermission[];
  /** Whether THIS client_user has TOTP enrolled - 2FA is optional/self-service for tenant staff
   * (docs/WALKTHROUGH.md "2FA"), so the Security settings page needs this to show "Enable 2FA"
   * vs "Reset 2FA". */
  totpEnabled: boolean;
  /** ASSUMED field - see lib/types.ts's `TenantStatus` doc comment. `null` while loading/
   * unauthenticated, same "don't render as if we positively know" rule `ClientPackageState` uses.
   * Backs `components/ModeSwitch.tsx`'s `SetupModeBanner` (dashboard brief task 2). */
  tenantStatus: TenantStatus | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  hasPermission: (p: TenantPermission) => boolean;
  refresh: () => Promise<void>;
}

const AuthContext = createContext<AuthState>({
  tenantId: null,
  email: null,
  permissions: [],
  totpEnabled: false,
  tenantStatus: null,
  isLoading: true,
  isAuthenticated: false,
  hasPermission: () => false,
  refresh: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const meQuery = useQuery({
    queryKey: ['dashboard-me'],
    queryFn: api.me.get,
    retry: false,
    staleTime: 60_000,
  });

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['dashboard-me'] });
  }, [queryClient]);

  const value = useMemo<AuthState>(() => {
    const permissions = (meQuery.data?.permissions ?? []) as TenantPermission[];
    return {
      tenantId: meQuery.data?.tenantId ?? null,
      email: meQuery.data?.email ?? null,
      permissions,
      totpEnabled: meQuery.data?.totpEnabled ?? false,
      tenantStatus: meQuery.data?.tenantStatus ?? null,
      isLoading: meQuery.isLoading,
      isAuthenticated: Boolean(meQuery.data) && !meQuery.isError,
      hasPermission: (p) => permissions.includes(p),
      refresh,
    };
  }, [meQuery.data, meQuery.isLoading, meQuery.isError, refresh]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  return useContext(AuthContext);
}

/**
 * RECONCILED (was an assumed/invented `settings.manage` string that doesn't exist in
 * `packages/rbac/src/permissions.ts`'s real `TENANT_PERMISSIONS`, so it denied even the Owner
 * role, which holds exactly that array - `OWNER: { permissions: [...TENANT_PERMISSIONS] }`,
 * `packages/rbac/src/presets.ts` - and would never have matched a nonexistent string). The real
 * backend routes this gates (`GET/PATCH /dashboard/refund-policy`, `/dashboard/receipt-settings`,
 * `apps/client-api/src/routes/dashboard/extras.ts`) actually check `orders.refund` - "every role
 * that can process a refund already holds it" is that file's own stated reasoning, and it's
 * correct: Owner has it via the full `TENANT_PERMISSIONS` list, Finance and Manager hold it too
 * (`packages/rbac/src/presets.ts`). Using the SAME permission string here means the UI and the
 * server agree, instead of silently hiding this screen from everyone including the Owner.
 */
export const SETTINGS_MANAGE_PERMISSION: TenantPermission = 'orders.refund';

/** Courtesy-only gate (MASTER_PROMPT.md / RBAC.md: "the UI hides what the server already blocks,
 * never the reverse"). Every mutating call still goes to apps/api, which re-checks permissions
 * server-side regardless of what this component renders. */
export function RequirePermission({
  permission,
  children,
  fallback = null,
}: {
  permission: TenantPermission;
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const { hasPermission, isLoading } = useAuth();
  if (isLoading) return null;
  return hasPermission(permission) ? <>{children}</> : <>{fallback}</>;
}
